import { Popover, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import { borderStyle, smallGap } from "../../../styles/consts";
import { SmallText } from "../../styles/Text.styles";
import Dropdown from "../fundamentals/Dropdown";

export type FilterType = "text" | "date" | "select" | "number";

export type FilterConfig = {
  type: FilterType;
  options?: { value: string; name: string }[];
  min?: number;
  max?: number;
  includeGlobalFilter?: boolean;
};

export type FilterValue = {
  type: FilterType;
  value:
    | string
    | { min: Date | null; max: Date | null }
    | { min: number | null; max: number | null }
    | null;
};

type MinMaxInputProps = {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  min?: number;
  max?: number;
};

const MinMaxInput = ({ label, value, onChange, onKeyDown, min, max }: MinMaxInputProps) => (
  <Stack>
    <SmallText>{label}</SmallText>
    <TextField
      type="number"
      value={value || ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      onKeyDown={onKeyDown}
      inputProps={{ min, max }}
    />
  </Stack>
);

type MinMaxDateInputProps = {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  isEndDate?: boolean;
};

const MinMaxDateInput = ({ label, value, onChange, isEndDate }: MinMaxDateInputProps) => (
  <Stack>
    <SmallText>{label}</SmallText>
    <DatePicker
      value={value}
      onChange={(date) => {
        if (date) {
          if (isEndDate) {
            date.setHours(23, 59, 59, 999);
          } else {
            date.setHours(0, 0, 0, 0);
          }
        }
        onChange(date);
      }}
      format="dd/MM/yyyy"
      maxDate={new Date()}
    />
  </Stack>
);

const MinMaxContainer = ({ children }: { children: React.ReactNode }) => (
  <Stack
    sx={{
      padding: smallGap,
      border: borderStyle,
      flexDirection: "row",
      gap: smallGap,
    }}
  >
    {children}
  </Stack>
);

export default function ColumnFilter({
  anchorEl,
  onClose,
  onApply,
  config,
  initialValue,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (value: FilterValue) => void;
  config: FilterConfig;
  initialValue?: FilterValue;
}) {
  const [value, setValue] = useState<FilterValue>(
    initialValue || {
      type: config.type,
      value: config.type === "text" ? "" : null,
    }
  );

  const open = Boolean(anchorEl);

  const handleApply = () => {
    onApply(value);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  const renderFilterInput = () => {
    switch (config.type) {
      case "text":
        return (
          <TextField
            value={value.value || ""}
            onChange={(e) => setValue({ ...value, value: e.target.value })}
            onKeyDown={handleKeyDown}
            placeholder="Enter text..."
            autoComplete="off"
          />
        );
      case "date": {
        const currentValue = (value.value as { min: Date | null; max: Date | null }) || { min: null, max: null };
        return (
          <MinMaxContainer>
            <MinMaxDateInput
              label="Start date"
              value={currentValue.min}
              onChange={(date) => setValue({
                ...value,
                value: { ...currentValue, min: date },
              })}
            />
            <MinMaxDateInput
              label="End date"
              value={currentValue.max}
              onChange={(date) => setValue({
                ...value,
                value: { ...currentValue, max: date },
              })}
              isEndDate
            />
          </MinMaxContainer>
        );
      }
      case "select":
        return (
          <Dropdown
            options={config.options || []}
            initialSelected={value.value as string}
            onChange={(val) => {
              const newValue = { ...value, value: val };
              setValue(newValue);
              onApply(newValue);
              onClose();
            }}
          />
        );
      case "number": {
        const currentNumberValue = (value.value as { min: number | null; max: number | null }) || { min: null, max: null };
        return (
          <MinMaxContainer>
            <MinMaxInput
              label="Min"
              value={currentNumberValue.min}
              onChange={(val) => setValue({
                ...value,
                value: { ...currentNumberValue, min: val },
              })}
              onKeyDown={handleKeyDown}
              min={config.min}
              max={config.max}
            />
            <MinMaxInput
              label="Max"
              value={currentNumberValue.max}
              onChange={(val) => setValue({
                ...value,
                value: { ...currentNumberValue, max: val },
              })}
              onKeyDown={handleKeyDown}
              min={config.min}
              max={config.max}
            />
          </MinMaxContainer>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        handleApply();
        onClose();
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            boxShadow: "none",
            borderRadius: "0",
            overflow: "visible",
            "& .MuiPaper-root": {
              overflow: "visible",
            },
          },
        },
      }}
    >
      {renderFilterInput()}
    </Popover>
  );
}
